/**
 * An application in DEFINE_COURSES has been created but never submitted — the
 * guided wizard is the only place it can be worked on, so both detail routes bounce
 * to the wizard rather than rendering a read-only view of an empty draft.
 *
 * Note this is a DRAFT redirect, not a rejection one. The "request changes" states
 * (COURSES_NOT_APPROVED / EVIDENCE_NOT_APPROVED) are deliberately absent: the
 * backend will not accept `request_courses_approval` or `request_evidence_approval`
 * from them (see tas_application.rb), so sending an employee into the wizard from
 * there would end in a failed submit. Correcting a rejection needs the backend's
 * `define_courses` AASM event first — DecisionNotice's resubmit CTA fires it via
 * the `define-courses` transition endpoint (defineCoursesRequest) and then lands
 * here as an ordinary DEFINE_COURSES draft (#10286/#10287).
 */
const DRAFT_STATE = 'TAS.ApplicationState.DEFINE_COURSES';

/** Every application type routes to its own `-guided` wizard for the `new` flow;
 * both 4.x variants share the type-four wizard. */
const GUIDED_WIZARD_ROUTE_BY_TYPE: Record<string, string> = {
  'TAS.ProgramType.2': 'authenticated.tas.applications.type-two-guided.new',
  'TAS.ProgramType.3': 'authenticated.tas.applications.type-three-guided.new',
  'TAS.ProgramType.4.2': 'authenticated.tas.applications.type-four-guided.new',
  'TAS.ProgramType.4.3': 'authenticated.tas.applications.type-four-guided.new',
};

/**
 * The guided-wizard route an application should be redirected to, or `null` to let
 * the current route render.
 *
 * Shared by the legacy show route and the manage route so the two cannot disagree
 * about when a draft gets bounced — a second copy of this table is how they would
 * drift.
 */
export function guidedWizardRouteFor(
  typeClassification: string | null | undefined,
  state: string | null | undefined
): string | null {
  if (state !== DRAFT_STATE) {
    return null;
  }
  if (!typeClassification) {
    return null;
  }
  return GUIDED_WIZARD_ROUTE_BY_TYPE[typeClassification] ?? null;
}
